import { Box, Container, Typography } from "@material-ui/core"
import {
  BlessingResponse,
  GiftCardSettingModel,
  postGiftCardGetGiftCardSchedule,
} from "api"
import { useIsDesktop } from "common/MediaQueries"
import { productDetailView } from "common/analytics"
import BackButton from "components/BackButton/BackButton"
import GenericButton from "components/GenericButton/GenericButton"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { formatPhoneNumber } from "components/Reusable/Functions/Functions"
import { useRouter } from "next/router"
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import styled from "styled-components"
import Calendar from "./Calendar"
import GiftCardAmount from "./GIftCardAmount"
import GiftCardInfo from "./GiftCardInfo"
import GiftCardEndTime from "./GiftCardScheduler/GiftCardEndTime"
import GiftCardFrequency from "./GiftCardScheduler/GiftCardFrequency"
import GiftCardScheduler from "./GiftCardScheduler/GiftCardScheduler"
import GiftCardStartTime from "./GiftCardScheduler/GiftCardStartTime"
import GiftCardStaticText from "./GiftCardScheduler/GiftCardStaticText"
import GiftCardSummaryText from "./GiftCardScheduler/GiftCardSummaryText"
import LegalCheckboxes from "./LegalCheckboxes"
import MyPersonalFields from "./MyPersonalFields"
import OtherPersonFields from "./OtherPersonFields"
import PurchaseButton from "./PurchaseButton"
import { usePurchaseContext } from "./PurchaseContext"
import QuantityBox from "./QuantityBox"
import SchemaPurchase from "./SchemaPurchase"

type PurchaseProcessProps = {
  giftCard: BlessingResponse
  setting: GiftCardSettingModel
  setRefList: Dispatch<SetStateAction<any[]>>
}

const StyledWrapper = styled(Container)`
  padding: 94px 16px 80px 16px;

  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    padding: 120px 60px 80px 60px;
  }
`

const StyledBackButton = styled(BackButton)`
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 48px;
  }
`

const StyledTitle = styled(Typography).attrs({
  component: "h1",
  variant: "h2",
})`
  color: #000;
  text-align: right;
  margin-top: 20px;
  margin-bottom: 20px;
  line-height: 0.8;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 40px;
    width: 500px;
  }
`

const StyledError = styled.p`
  color: #d70202;
  font-size: 20px;
  text-align: right;
  line-height: 1.1;
`

const StyledButtonBox = styled(Box)`
  display: flex;
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-bottom: 20px;
  }
`

const StyledFieldWrapper = styled(Box)`
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: flex-start;
  }
`

const StyledCardWrapper = styled(Box)`
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: flex-start;
  }
`

const StyledBoxWrap = styled(Box)`
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 760px;
  }
`

const StyledScheduleWrapper = styled.div`
  position: relative;
`

const StyledScheduleError = styled(Typography).attrs({ variant: "body1" })`
  color: #d70202;
  font-size: 18px;
  line-height: 1;
  text-align: right;
  position: absolute;
  bottom: -20px;
`

const PurchaseProcess = ({
  giftCard,
  setting,
  setRefList,
}: PurchaseProcessProps) => {
  const {
    purchaseData,
    setPurchaseData,
    setBlessingMode,
    setSelectedGiftCard,
    categoryName,
    prevPath,
    blessingMode,
    setSchedulerType,
    schedulerType,
  } = usePurchaseContext()
  const router = useRouter()

  const Section1Ref = useRef(null)
  const amountRef = useRef(null)

  useEffect(() => {
    setRefList([Section1Ref])
  }, [])

  const blessingType = giftCard?.blessingType
  const [selectedMode, setSelectedMode] = useState(
    blessingMode.length
      ? blessingMode
      : blessingType === "Both" || blessingType === "Another"
      ? "full"
      : "short"
  )
  const [quantityError, setQuantityError] = useState(false)
  const [amountError, setAmountError] = useState("")
  const [noValueValidation, setNoValueValidation] = useState(false)
  const [quantity, setQuantity] = useState(purchaseData?.quantity || 0)
  const [scheduleType, setScheduleType] = useState(
    schedulerType
      ? schedulerType
      : giftCard?.blessingScheduleTypeDefault
      ? giftCard.blessingScheduleTypeDefault
      : '"AfterPayment"'
  )
  const [scheduleStartTime, setScheduleStartTime] = useState(
    purchaseData?.allowanceStartDateTime
      ? purchaseData.allowanceStartDateTime
      : null
  )
  const [scheduleEndTime, setScheduleEndTime] = useState(
    purchaseData?.allowanceEndDateTime
      ? purchaseData.allowanceEndDateTime
      : null
  )
  const [frequency, setFrequency] = useState(
    purchaseData?.giftCardScheduleType
      ? purchaseData.giftCardScheduleType
      : null
  )
  const [chargeAmount, setChargeAmount] = useState(0)

  const isDesktop = useIsDesktop()
  const regex = /^[a-zA-Zא-תء-ي'"‘’\\.\-,\s]+$/

  const [date, setDate] = useState(
    purchaseData?.messagesScheduleDate
      ? purchaseData?.messagesScheduleDate
      : null
  )
  const [scheduleError, setScheduleError] = useState(null)
  const [scheduleTypeError, setScheduleTypeError] = useState(null)
  const [pastTimeError, setPastTimeError] = useState(null)

  useEffect(() => {
    if (!schedulerType) setSchedulerType(scheduleType)
  }, [scheduleType])

  useEffect(() => {
    if (scheduleStartTime) {
      if (new Date(scheduleStartTime) > new Date()) {
        setPastTimeError(null)
      }
    }
    if (scheduleEndTime && scheduleStartTime) {
      const startTime = new Date(scheduleStartTime)
      const endTime = new Date(scheduleEndTime)
      endTime.setHours(startTime.getHours(), startTime.getMinutes())

      const getfrequency = async () => {
        const data = {
          startDateTime: scheduleStartTime,
          endDateTime: endTime.toISOString(),
          giftCardScheduleType: frequency,
        }

        const response = await postGiftCardGetGiftCardSchedule(data)
        setChargeAmount(response.data.body.schedule.length)
      }

      getfrequency()
    }
  }, [scheduleEndTime, scheduleStartTime])

  useEffect(() => {
    giftCard && productDetailView(categoryName, giftCard.id, giftCard.name)
  }, [giftCard])

  useEffect(() => {
    if (router.query && router.query.focus && document) {
      const input = document?.getElementById(router.query.focus.toString())
      input?.scrollIntoView()
      setTimeout(() => {
        input?.focus()
      }, 1500)
    }
  }, [router])

  const initialValues =
    blessingType === "Both" || blessingType === "Another"
      ? {
          amount: purchaseData?.amount || "",
          recipientName: purchaseData?.recipientName || "",
          phoneNumber: purchaseData?.phoneNumber || "",
          recipientEmail: purchaseData?.recipientEmail?.trim() || "",
          senderBlessing: purchaseData?.senderBlessing || "",
          senderName: purchaseData?.senderName || "",
          senderLastName: purchaseData?.senderLastName || "",
          senderMobilePhone: purchaseData?.senderMobilePhone || "",
          senderEmail: purchaseData?.senderEmail?.trim() || "",
          checkedOne: purchaseData?.["checkedOne"] || false,
          checkedTwo: purchaseData?.["checkedTwo"] || false,
          quantity: purchaseData?.quantity || 0,
        }
      : {
          amount: purchaseData?.amount || "",
          senderName: purchaseData?.senderName || "",
          senderLastName: purchaseData?.senderLastName || "",
          senderMobilePhone: purchaseData?.senderMobilePhone || "",
          senderEmail: purchaseData?.senderEmail?.trim() || "",
          checkedOne: purchaseData?.["checkedOne"] || false,
          checkedTwo: purchaseData?.["checkedTwo"] || false,
          quantity: purchaseData?.quantity || 0,
        }

  const handleSelectMode = (mode: string) => {
    setSelectedMode(mode)
    setBlessingMode(mode)
  }

  const buttonValid = (valid: boolean, touched: object, amount: number) => {
    return (
      valid &&
      (Object.keys(touched).length > 0 || purchaseData) &&
      !amountError &&
      quantity > 0 &&
      amount > 0
    )
  }

  const saveGiftCardData = (values: object) => {
    if (scheduleType === "Allowance") {
      purchaseData.allowanceStartDateTime = scheduleStartTime
      purchaseData.allowanceEndDateTime = scheduleEndTime
      purchaseData.giftCardScheduleType = frequency
    }
    if (scheduleType === "Scheduled" && date) {
      purchaseData.messagesScheduleDate = date
      // clear the allowance fields
      delete purchaseData["allowanceStartDateTime"]
      delete purchaseData["allowanceEndDateTime"]
      delete purchaseData["giftCardScheduleType"]
    }
    setPurchaseData({ ...purchaseData, ...values })
  }

  const validationSchema = useMemo(() => {
    return selectedMode === "full"
      ? SchemaPurchase.schemaPurchaseFull
      : SchemaPurchase.schemaPurchaseShort
  }, [selectedMode])

  const onSubmit = (values) => {
    if (schedulerType === "Scheduled" && date) {
      values.messagesScheduleDate = date
    } else {
      if (purchaseData?.messagesScheduleDate) {
        delete values["messagesScheduleDate"]
        delete purchaseData["messagesScheduleDate"]
      }
    }

    if (scheduleType === "Allowance") {
      const startTime = new Date(scheduleStartTime)
      const endTime = new Date(scheduleEndTime)
      endTime.setHours(startTime.getHours(), startTime.getMinutes())
      values.allowanceStartDateTime = scheduleStartTime
      values.allowanceEndDateTime = endTime
      values.giftCardScheduleType = frequency
    } else if (
      purchaseData?.allowanceStartDateTime ||
      purchaseData?.allowanceEndDateTime ||
      purchaseData?.giftCardScheduleType
    ) {
      delete values["allowanceStartDateTime"]
      delete values["allowanceEndDateTime"]
      delete values["giftCardScheduleType"]
      delete purchaseData["allowanceStartDateTime"]
      delete purchaseData["allowanceEndDateTime"]
      delete purchaseData["giftCardScheduleType"]
    }

    values.amount = parseInt(values.amount.toString().replace(/[₪]/g, ""))
    setPurchaseData({ ...purchaseData, ...values })
    setBlessingMode(selectedMode)
    setSelectedGiftCard(giftCard)
    router.push("/preview")
  }

  const showOveralError = (touched) => {
    const error = "*בבקשה לבדוק שהשלמת את כל הפרטים נכון"
    if (
      Object.keys(touched).length === 1 &&
      Object.prototype.hasOwnProperty.call(touched, "senderBlessing")
    ) {
      return ""
    } else {
      return error
    }
  }

  return (
    <StyledWrapper>
      {!isDesktop && (
        <BackButton
          linkText="חזרה"
          prevPath={
            giftCard?.isAiImage ? "/create-ai/choose-picture" : prevPath
          }
        />
      )}
      {!isDesktop && <GiftCardInfo giftCard={giftCard} />}
      <FormikForm
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={async (values) => {
          const amount = parseInt(values.amount.toString().replace(/[₪]/g, ""))
          if (!amountError && amount > 0 && values.quantity > 0) {
            onSubmit(values)
          }
        }}
        alwaysValidate
        render={(formik) => {
          return (
            <>
              <StyledCardWrapper>
                {isDesktop && <GiftCardInfo giftCard={giftCard} />}
                <StyledBoxWrap>
                  {isDesktop && (
                    <StyledBackButton
                      linkText="חזרה"
                      prevPath={
                        giftCard?.isAiImage
                          ? "/create-ai/choose-picture"
                          : prevPath
                      }
                    />
                  )}
                  <StyledTitle ref={Section1Ref}>
                    הזנת פרטים לרכישת גיפטקארד מסוג - {giftCard?.name}
                  </StyledTitle>
                  <PurchaseButton
                    blessingType={giftCard?.blessingType}
                    handleSelectMode={handleSelectMode}
                    formik={formik}
                    blessingMode={blessingMode}
                  />
                  <GiftCardAmount
                    setAmountError={setAmountError}
                    amountError={amountError}
                    setQuantity={setQuantity}
                    amounts={setting?.giftCardAmounts || []}
                    maxAmuont={setting?.maxAmuont || 1500}
                    minAmount={setting?.minamuont || 1}
                    amountRef={amountRef}
                    setNoValueValidation={setNoValueValidation}
                    noValueValidation={noValueValidation}
                    validateForm={formik.validateForm}
                    purchaseData={purchaseData}
                    promotion={giftCard?.promotion?.name}
                  />
                  <QuantityBox
                    quantityError={quantityError}
                    setQuantityError={setQuantityError}
                    setAmountError={setAmountError}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    maxGiftCard={setting?.maxGiftCard || 5}
                    maxAmuont={setting?.maxAmuont || 1500}
                    minAmount={setting?.minamuont || 1}
                  />
                </StyledBoxWrap>
              </StyledCardWrapper>

              <GiftCardScheduler
                setScheduleType={setScheduleType}
                scheduleType={scheduleType}
                setSchedulerType={setSchedulerType}
              />

              {scheduleType === "Allowance" && (
                <>
                  <GiftCardStaticText
                    amount={formik.values.amount ? formik.values.amount : 0}
                    quantity={
                      formik.values.quantity ? formik.values.quantity : 1
                    }
                  />
                  <GiftCardStartTime
                    setScheduleStartTime={setScheduleStartTime}
                    scheduleStartTime={scheduleStartTime}
                    scheduleError={scheduleError}
                    setScheduleEndTime={setScheduleEndTime}
                    pastTimeError={pastTimeError}
                  />
                  <GiftCardFrequency
                    setFrequency={setFrequency}
                    scheduleError={scheduleError}
                    frequency={frequency}
                    setScheduleEndTime={setScheduleEndTime}
                  />
                  <GiftCardEndTime
                    setScheduleEndTime={setScheduleEndTime}
                    scheduleEndTime={scheduleEndTime}
                    frequency={frequency}
                    scheduleStartTime={scheduleStartTime}
                    scheduleError={scheduleError}
                  />
                  {chargeAmount > 0 && scheduleStartTime && scheduleEndTime && (
                    <GiftCardSummaryText
                      price={formik.values.amount}
                      amount={formik.values.quantity}
                      startDateTime={scheduleStartTime}
                      endDateTime={scheduleEndTime}
                      chargeAmount={chargeAmount}
                    />
                  )}
                </>
              )}

              {scheduleType === "Scheduled" && (
                <StyledScheduleWrapper>
                  <Calendar date={date} setDate={setDate} type="schedule" />
                  {scheduleTypeError && !date && (
                    <StyledScheduleError>
                      {scheduleTypeError}
                    </StyledScheduleError>
                  )}
                </StyledScheduleWrapper>
              )}

              <StyledFieldWrapper>
                {selectedMode === "full" && (
                  <OtherPersonFields
                    formatPhoneNumber={formatPhoneNumber}
                    saveGiftCardData={saveGiftCardData}
                    regex={regex}
                  />
                )}
                <MyPersonalFields
                  selectedMode={selectedMode}
                  formatPhoneNumber={formatPhoneNumber}
                  regex={regex}
                />
              </StyledFieldWrapper>
              <LegalCheckboxes saveGiftCardData={saveGiftCardData} />

              {pastTimeError ? (
                <StyledError>{pastTimeError}</StyledError>
              ) : !formik.isValid && formik.submitCount ? (
                <StyledError>{showOveralError(formik.touched)}</StyledError>
              ) : null}

              <StyledButtonBox>
                <GenericButton
                  btnType="filled"
                  width={328}
                  onClick={(e) => {
                    e.preventDefault()
                    // If the amount is empty, focus the amount field and show an error message.
                    if (!formik.values.amount) {
                      setNoValueValidation(true)
                      const amountElLabel = document.querySelector(
                        "[name='amount']"
                      )?.nextSibling as HTMLLabelElement
                      amountElLabel.focus()
                      return
                    }
                    if (scheduleType === "Scheduled") {
                      if (!date) {
                        setScheduleTypeError("שדה זה הינו שדה חובה")
                        return
                      }
                      if (date) {
                        setScheduleTypeError(null)
                      }
                    }
                    if (scheduleType === "Allowance") {
                      if (
                        !scheduleStartTime ||
                        !scheduleEndTime ||
                        !frequency
                      ) {
                        setScheduleError("שדה זה הינו שדה חובה")
                        return
                      }
                      if (new Date(scheduleStartTime) < new Date()) {
                        setPastTimeError("יש לבחור מועד עתידי לשליחת הגיפטקארד")
                        return
                      }
                      if (scheduleEndTime && scheduleStartTime && frequency) {
                        setScheduleError(null)
                      }
                      if (new Date(scheduleStartTime) > new Date()) {
                        setPastTimeError(null)
                      }
                    }
                    // Otherwise, submit the form.
                    formik.handleSubmit()
                  }}
                  type="submit"
                  isdisabled={
                    !(
                      buttonValid(
                        formik.isValid,
                        formik.touched,
                        parseInt(
                          formik.values.amount.toString().replace(/[₪]/g, "")
                        )
                      ) &&
                      (scheduleType === "Scheduled"
                        ? date
                          ? true
                          : false
                        : scheduleType === "Allowance"
                        ? scheduleEndTime &&
                          scheduleStartTime &&
                          frequency &&
                          new Date(scheduleStartTime) > new Date()
                          ? true
                          : false
                        : true)
                    )
                  }
                >
                  המשך לסיכום פרטים
                </GenericButton>
              </StyledButtonBox>
            </>
          )
        }}
      />
    </StyledWrapper>
  )
}

export default PurchaseProcess
